import type { EducationMenuItem } from '../layout/types'

const BASE = '/ai-Application-center/ai-education'

const K12_STAGES = new Set(['primary', 'junior', 'senior'])

/** 角色工作台入口（按账号 membership / workspace 过滤） */
const ROLE_WORKBENCHES: Array<EducationMenuItem & { workspace: string }> = [
  {
    workspace: 'teacher',
    title: '教师工作台',
    path: `${BASE}/teacher`,
    icon: 'ep:user',
    activePaths: [`${BASE}/teacher`]
  },
  {
    workspace: 'student',
    title: '学生工作台',
    path: `${BASE}/student/home`,
    icon: 'ep:reading',
    activePaths: [`${BASE}/student`]
  },
  {
    workspace: 'parent',
    title: '家长工作台',
    path: `${BASE}/parent/home`,
    icon: 'ep:house',
    activePaths: [`${BASE}/parent`]
  },
  {
    workspace: 'principal',
    title: '校长工作台',
    path: `${BASE}/principal/home`,
    icon: 'ep:office-building',
    activePaths: [`${BASE}/principal`]
  },
  {
    workspace: 'researcher',
    title: '教研员工作台',
    path: `${BASE}/researcher/home`,
    icon: 'ep:data-analysis',
    activePaths: [`${BASE}/researcher`]
  },
  {
    workspace: 'regionAdmin',
    title: '区域管理工作台',
    path: `${BASE}/regionAdmin/home`,
    icon: 'ep:location',
    activePaths: [`${BASE}/regionAdmin`]
  },
  {
    workspace: 'platformAdmin',
    title: '平台管理工作台',
    path: `${BASE}/platformAdmin/home`,
    icon: 'ep:monitor',
    activePaths: [`${BASE}/platformAdmin`]
  }
]

/** 教务/运营管理菜单（学校管理员、教务、超管） */
const ADMIN_MENUS: EducationMenuItem[] = [
  { title: '概览', path: `${BASE}/overview`, icon: 'ep:home-filled' },
  { title: '基础配置', path: `${BASE}/configuration`, icon: 'ep:setting' },
  {
    title: '家长关系',
    path: `${BASE}/parent-relation`,
    icon: 'ep:avatar',
    activePaths: [`${BASE}/parent-relation`],
    /** K12 默认展示；大学仅 parent_enabled 时可见 */
    stageScope: 'parent'
  },
  {
    title: '年级',
    path: `${BASE}/configuration`,
    query: { tab: 'organization', orgTab: 'grade' },
    icon: 'ep:school',
    activePaths: [`${BASE}/configuration`],
    stageScope: 'k12'
  },
  {
    title: '行政班',
    path: `${BASE}/configuration`,
    query: { tab: 'organization', orgTab: 'class' },
    icon: 'ep:house',
    activePaths: [`${BASE}/configuration`],
    stageScope: 'k12'
  },
  {
    title: '邀请码',
    path: `${BASE}/configuration`,
    query: { tab: 'member' },
    icon: 'ep:ticket',
    activePaths: [`${BASE}/configuration`],
    stageScope: 'k12'
  },
  { title: '教学与AI治理', path: `${BASE}/learning`, icon: 'ep:reading' },
  { title: '学情分析', path: `${BASE}/analytics`, icon: 'ep:data-analysis' },
  { title: '内容与运营', path: `${BASE}/operations`, icon: 'ep:box' },
  {
    title: '学期校历',
    path: `${BASE}/term`,
    icon: 'ep:calendar',
    activePaths: [`${BASE}/term`],
    stageScope: 'university'
  },
  {
    title: '开课教学班',
    path: `${BASE}/offering`,
    icon: 'ep:collection',
    activePaths: [`${BASE}/offering`]
  },
  {
    title: '选课候补',
    path: `${BASE}/enrollment`,
    icon: 'ep:tickets',
    activePaths: [`${BASE}/enrollment`],
    stageScope: 'university'
  },
  {
    title: '课表排课',
    path: `${BASE}/timetable`,
    icon: 'ep:clock',
    activePaths: [`${BASE}/timetable`]
  },
  {
    title: '考勤中心',
    path: `${BASE}/attendance`,
    icon: 'ep:finished',
    activePaths: [`${BASE}/attendance`]
  },
  {
    title: '直播场次',
    path: `${BASE}/teach-session`,
    icon: 'ep:video-camera',
    activePaths: [`${BASE}/teach-session`]
  },
  {
    title: '成绩双审',
    path: `${BASE}/gradebook`,
    icon: 'ep:medal',
    activePaths: [`${BASE}/gradebook`],
    stageScope: 'university'
  }
]

export interface BuildEducationMenuOptions {
  /** 当前账号可用的工作台类型（来自 organizations[].workspace） */
  availableWorkspaces?: string[]
  /** 当前选中的工作台 */
  currentWorkspace?: string
  /** 平台超管：展示全部角色入口 + 教务菜单 */
  isPlatformAdmin?: boolean
  /** 是否具备教务管理菜单（有学校/学期等管理权限） */
  showAdminMenus?: boolean
  /** 教师仅展示教学相关侧栏（不含基础配置/学期校历等） */
  showTeacherMenus?: boolean
  /** 当前学校学段（字典 ai_edu_stage） */
  eduStage?: string
  /** 当前学校是否开启家长视图 */
  parentEnabled?: boolean
}

/** 教师可见的精简教务入口（不含基础配置等管理项） */
const TEACHER_MENUS: EducationMenuItem[] = [
  { title: '教学与AI治理', path: `${BASE}/learning`, icon: 'ep:reading' },
  {
    title: '开课教学班',
    path: `${BASE}/offering`,
    icon: 'ep:collection',
    activePaths: [`${BASE}/offering`]
  },
  {
    title: '课表排课',
    path: `${BASE}/timetable`,
    icon: 'ep:clock',
    activePaths: [`${BASE}/timetable`]
  },
  {
    title: '考勤中心',
    path: `${BASE}/attendance`,
    icon: 'ep:finished',
    activePaths: [`${BASE}/attendance`]
  }
]

/** 学生工作台侧栏（与 student/Home 等页一致） */
export const STUDENT_MENUS: EducationMenuItem[] = [
  { title: '学习首页', path: `${BASE}/student/home`, icon: 'ep:home-filled' },
  { title: '我的课程', path: `${BASE}/student/courses`, icon: 'ep:reading' },
  { title: '我的作业', path: `${BASE}/student/assignments`, icon: 'ep:document-checked' },
  { title: '学习计划', path: `${BASE}/student/learning-plans`, icon: 'ep:calendar' },
  { title: '学习报告', path: `${BASE}/student/learning-report`, icon: 'ep:data-analysis' },
  { title: '错题本', path: `${BASE}/student/wrong-answers`, icon: 'ep:warning' },
  { title: 'AI 学伴', path: `${BASE}/student/assistant`, icon: 'ep:magic-stick' },
  { title: '我的资料', path: `${BASE}/student/profile`, icon: 'ep:user' }
]

function isK12(eduStage?: string) {
  return !!eduStage && K12_STAGES.has(eduStage)
}

function filterAdminMenus(
  menus: EducationMenuItem[],
  eduStage?: string,
  parentEnabled?: boolean
): EducationMenuItem[] {
  const k12 = isK12(eduStage)
  return menus.filter((item) => {
    const scope = (item as EducationMenuItem & { stageScope?: string }).stageScope
    if (!scope) return true
    if (scope === 'university') return !k12
    if (scope === 'parent') {
      if (k12) return true
      return parentEnabled === true
    }
    if (scope === 'k12') return k12
    return true
  })
}

/**
 * 组装侧栏：角色工作台（按身份） + 教务管理（按权限 / 学段）。
 */
export function buildEducationMenuItems(options: BuildEducationMenuOptions = {}): EducationMenuItem[] {
  const {
    availableWorkspaces = [],
    currentWorkspace,
    isPlatformAdmin = false,
    showAdminMenus = true,
    showTeacherMenus = false,
    eduStage,
    parentEnabled
  } = options

  const workspaceSet = new Set(
    isPlatformAdmin
      ? ROLE_WORKBENCHES.map((item) => item.workspace)
      : availableWorkspaces.length
        ? availableWorkspaces
        : currentWorkspace
          ? [currentWorkspace]
          : ['teacher']
  )

  // 大学默认隐藏家长工作台入口（除非学校显式开启）
  if (!isK12(eduStage) && parentEnabled !== true) {
    workspaceSet.delete('parent')
  }

  const roleItems = ROLE_WORKBENCHES.filter((item) => workspaceSet.has(item.workspace)).map(
    ({ workspace: _w, ...item }) => item
  )

  const items: EducationMenuItem[] = []

  if (roleItems.length) {
    items.push({
      title: '角色工作台',
      path: '',
      icon: '',
      section: true
    })
    items.push(...roleItems)
  }

  if (showAdminMenus || isPlatformAdmin) {
    items.push({
      title: '教务管理',
      path: '',
      icon: '',
      section: true
    })
    items.push(...filterAdminMenus(ADMIN_MENUS, eduStage, parentEnabled))
  } else if (showTeacherMenus) {
    items.push({
      title: '教学工作',
      path: '',
      icon: '',
      section: true
    })
    items.push(...TEACHER_MENUS)
  }

  return items
}

/** 兼容旧调用：管理端静态兜底展示全部角色入口 + 教务菜单 */
export function getAdminEducationMenuItems(): EducationMenuItem[] {
  return buildEducationMenuItems({
    isPlatformAdmin: true,
    showAdminMenus: true,
    eduStage: 'university',
    parentEnabled: true
  })
}

export { BASE as AI_EDUCATION_BASE, ROLE_WORKBENCHES, ADMIN_MENUS, TEACHER_MENUS, K12_STAGES, isK12 }
